import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  FiHome, 
  FiDollarSign, 
  FiTarget, 
  FiLogOut, 
  FiMenu, 
  FiUser, 
  FiBell, 
  FiSettings, 
  FiChevronDown,
  FiChevronRight,
  FiShield,
  FiCreditCard,
  FiX,
  FiClock,
  FiCheckCircle,
  FiExternalLink,
  FiSun,
  FiMoon,
  FiWifi,
  FiCheckSquare,
  FiBriefcase,
  FiLayers,
  FiLock,
  FiAlertTriangle
} from 'react-icons/fi';
import { Bot, Sparkles, Database } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Inactive subscription modal state
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);

  // Sidebar expand/collapse state with localStorage persistence
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('finance_sidebar_open');
    return saved !== null ? saved === 'true' : true;
  });

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Submenu expansion state (Carteiras)
  const [carteirasExpanded, setCarteirasExpanded] = useState(() => {
    return window.location.pathname.startsWith('/carteiras');
  });

  useEffect(() => {
    if (location.pathname.startsWith('/carteiras')) {
      setCarteirasExpanded(true);
    }
  }, [location.pathname]);

  // Topbar dropdown states
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // System status and notifications data
  const [dbConnected, setDbConnected] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Dark/Light Theme state with localStorage persistence
  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem('finance_theme') === 'dark' || document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('finance_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('finance_theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  // Realtime Database connection status modal state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [connectionTime, setConnectionTime] = useState(() => {
    const d = new Date();
    return d.toLocaleTimeString('pt-BR');
  });

  const profileRef = useRef(null);
  const notifRef = useRef(null);

  // Toggle sidebar on desktop
  const toggleSidebar = () => {
    const nextState = !sidebarOpen;
    setSidebarOpen(nextState);
    localStorage.setItem('finance_sidebar_open', String(nextState));
  };

  // Sync body class for main layout shifting
  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add('sidebar-expanded');
      document.body.classList.remove('sidebar-collapsed');
    } else {
      document.body.classList.add('sidebar-collapsed');
      document.body.classList.remove('sidebar-expanded');
    }
  }, [sidebarOpen]);

  // Load DB status & notifications
  useEffect(() => {
    const checkDbStatus = async () => {
      try {
        const res = await axios.get(`${API}/system/status`);
        setDbConnected(res.data?.database === 'connected');
      } catch {
        setDbConnected(false);
      }
    };

    const loadNotifications = async () => {
      try {
        const res = await axios.get(`${API}/notifications/history?limit=5`, { withCredentials: true });
        const list = res.data?.notifications || [];
        setNotifications(list);
        setUnreadCount(list.length > 0 ? list.length : 0);
      } catch {
        // silent fallback
      }
    };

    checkDbStatus();
    loadNotifications();

    const interval = setInterval(() => {
      checkDbStatus();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const ADMIN_EMAILS = ['matheusrweber@gmail.com', 'keylenaiara13@gmail.com'];
  const isAdmin = Boolean(user?.email && ADMIN_EMAILS.includes(user.email));

  const isTrial = Boolean(
    !isAdmin &&
    user?.plan_type !== 'basic' &&
    user?.plan_type !== 'essential' &&
    (user?.trial_period || user?.plan_type === 'trial' || (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date()))
  );
  const isSubscriptionActive = isAdmin || user?.subscription_status === 'active' || isTrial;
  const isBasic = !isAdmin && isSubscriptionActive && user?.plan_type === 'basic';

  const handleMenuClick = (itemPath, isEssential) => {
    if (!isSubscriptionActive) {
      if (itemPath === '/assinatura' || itemPath === '/perfil' || itemPath === '/notificacoes' || itemPath === '/gerenciar' || itemPath === '/sessoes-ativas') {
        navigate(itemPath);
        return;
      }
      if (itemPath === '/dashboard') {
        navigate('/dashboard');
        return;
      }
      setInactiveModalOpen(true);
      return;
    }

    navigate(itemPath);
  };

  const menuItems = [
    { icon: FiHome, label: 'Dashboard', path: '/dashboard' },
    { icon: FiDollarSign, label: 'Registros', path: '/registros' },
    { icon: FiCheckSquare, label: 'Conciliação', path: '/conciliacao', isEssential: true },
    { icon: FiBriefcase, label: 'Carteiras', path: '/carteiras/movimentacoes', isEssential: true },
    { icon: FiTarget, label: 'Metas', path: '/metas' },
    { icon: Bot, label: 'Serafina IA', path: '/assistente' },
    { icon: FiBell, label: 'Notificações', path: '/notificacoes' },
    { icon: FiUser, label: 'Perfil', path: '/perfil' },
    { icon: FiCreditCard, label: 'Assinatura', path: '/assinatura' },
    ...(isAdmin ? [{ icon: FiSettings, label: 'Gerenciar', path: '/gerenciar' }] : []),
  ];

  // Get current page title
  const getPageTitle = () => {
    if (location.pathname === '/carteiras/movimentacoes' || location.pathname === '/carteiras') return 'Carteiras';
    if (location.pathname === '/carteiras/contas' || location.pathname === '/carteiras/categorias') return 'Plano de Contas';
    const current = menuItems.find(m => m.path === location.pathname);
    if (current) return current.label;
    if (location.pathname === '/conciliacao') return 'Conciliação Bancária';
    if (location.pathname === '/sessoes-ativas') return 'Sessões Ativas';
    if (location.pathname === '/subscription' || location.pathname === '/assinatura') return 'Assinatura';
    return 'Painel';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      {/* ================= LATERAL SIDEBAR (Desktop) ================= */}
      <aside
        className={`fixed top-0 left-0 bottom-0 z-40 bg-gradient-to-b from-[#071330] via-[#0C1E4E] to-[#122B68] text-white flex flex-col justify-between border-r border-white/10 shadow-2xl transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'w-64' : 'w-20'
        } hidden lg:flex`}
      >
        {/* Top Brand Header */}
        <div>
          <div className={`p-4 border-b border-white/10 flex items-center ${sidebarOpen ? 'justify-between gap-3' : 'justify-center'}`}>
            <div className="flex items-center gap-3 overflow-hidden">
              <div 
                onClick={() => navigate('/dashboard')}
                title="Finance - Gestão Inteligente"
                className="w-10 h-10 bg-gradient-to-tr from-blue-600 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25 border border-cyan-300/30 flex-shrink-0 cursor-pointer"
              >
                <FiDollarSign className="text-white" size={22} />
              </div>
              {sidebarOpen && (
                <div className="overflow-hidden whitespace-nowrap animate-in fade-in duration-200">
                  <h1 className="text-lg font-black tracking-tight text-white leading-none">Finance</h1>
                  <p className="text-[11px] text-cyan-300 font-medium tracking-wide mt-0.5">Gestão Inteligente</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="p-3 space-y-1.5 mt-2">
            {menuItems.map((item) => {
              const hasSub = Array.isArray(item.subItems) && item.subItems.length > 0;
              const isSubActive = hasSub && item.subItems.some(s => location.pathname === s.path || (s.path === '/carteiras/movimentacoes' && location.pathname === '/carteiras'));
              const active = location.pathname === item.path || isSubActive;
              const isEssentialItem = Boolean(item.isEssential);
              const isAssinaturaItem = item.path === '/assinatura';
              const isLocked = !isSubscriptionActive && !isAssinaturaItem && item.path !== '/perfil' && item.path !== '/notificacoes' && item.path !== '/gerenciar';
              const isPulsingAssinatura = isAssinaturaItem && !isSubscriptionActive;

              if (hasSub) {
                return (
                  <div key={item.label} className="space-y-1">
                    <button
                      onClick={() => {
                        if (!isSubscriptionActive) {
                          setInactiveModalOpen(true);
                          return;
                        }
                        if (!sidebarOpen) {
                          toggleSidebar();
                          setCarteirasExpanded(true);
                          navigate(item.path);
                        } else {
                          setCarteirasExpanded(!carteirasExpanded);
                        }
                      }}
                      title={!sidebarOpen ? item.label : undefined}
                      className={`w-full flex items-center justify-between rounded-xl font-medium text-sm transition-all group cursor-pointer ${
                        sidebarOpen ? 'px-3.5 py-2.5' : 'justify-center p-3'
                      } ${
                        active
                          ? 'bg-white/10 text-white font-semibold'
                          : 'text-slate-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`${isLocked ? 'p-0.5 rounded-lg ring-2 ring-amber-400/80 bg-amber-500/10' : ''}`}>
                          <item.icon size={19} className={`flex-shrink-0 ${active ? 'text-cyan-400' : isLocked ? 'text-amber-300' : 'text-slate-400 group-hover:scale-110 transition-transform'}`} />
                        </div>
                        {sidebarOpen && (
                          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                            {item.label}
                          </span>
                        )}
                      </div>
                      {sidebarOpen && (
                        <div className="flex items-center gap-1.5">
                          {isLocked ? (
                            <span className="text-[10px] font-extrabold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 leading-none tracking-tight flex items-center gap-1">
                              <FiLock size={10} />
                              Upgrade
                            </span>
                          ) : isEssentialItem && isTrial ? (
                            <span className="text-[10px] font-bold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-xs flex items-center gap-1 leading-none tracking-tight">
                              ⭐ Essencial
                            </span>
                          ) : isEssentialItem && isBasic ? (
                            <span className="text-[10px] font-extrabold uppercase px-2 py-[2px] rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 leading-none tracking-tight">
                              Upgrade
                            </span>
                          ) : null}
                          <div className="text-slate-400 transition-transform duration-200">
                            {carteirasExpanded ? <FiChevronDown size={15} /> : <FiChevronRight size={15} />}
                          </div>
                        </div>
                      )}
                    </button>

                    {/* Submenu Level 2 Items */}
                    {sidebarOpen && carteirasExpanded && (
                      <div className="pl-4 ml-4 space-y-1 border-l border-cyan-400/30 animate-in fade-in slide-in-from-top-1 duration-200">
                        {item.subItems.map((sub) => {
                          const subActive = location.pathname === sub.path || (sub.path === '/carteiras/movimentacoes' && location.pathname === '/carteiras');
                          return (
                            <button
                              key={sub.path}
                              onClick={() => {
                                if (!isSubscriptionActive) {
                                  setInactiveModalOpen(true);
                                  return;
                                }
                                navigate(sub.path);
                              }}
                              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg font-medium text-xs transition-all cursor-pointer ${
                                subActive
                                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold shadow-md shadow-cyan-500/20'
                                  : 'text-slate-300 hover:text-white hover:bg-white/5'
                              }`}
                            >
                              <sub.icon size={15} className={subActive ? 'text-white' : 'text-cyan-400/80'} />
                              <span>{sub.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={item.path}
                  onClick={() => handleMenuClick(item.path, isEssentialItem)}
                  title={!sidebarOpen ? item.label : undefined}
                  className={`w-full flex items-center rounded-xl font-medium text-sm transition-all group cursor-pointer ${
                    sidebarOpen ? 'gap-3 px-3.5 py-2.5' : 'justify-center p-3'
                  } ${
                    isPulsingAssinatura
                      ? 'border-2 border-amber-400 bg-amber-500/20 text-amber-300 font-bold shadow-lg shadow-amber-500/25 animate-pulse hover:bg-amber-500/30'
                      : active
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-cyan-500/25 font-semibold'
                      : 'text-slate-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <div className={`${isLocked ? 'p-0.5 rounded-lg ring-2 ring-amber-400/80 bg-amber-500/10' : ''}`}>
                    <item.icon size={19} className={`flex-shrink-0 ${isPulsingAssinatura ? 'text-amber-300' : active ? 'text-white' : isLocked ? 'text-amber-300' : 'text-cyan-400 group-hover:scale-110 transition-transform'}`} />
                  </div>
                  {sidebarOpen && (
                    <div className="flex items-center justify-between flex-1 overflow-hidden">
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis animate-in fade-in duration-200">
                        {item.label}
                      </span>
                      {isPulsingAssinatura ? (
                        <span className="ml-auto text-[10px] font-black uppercase px-2.5 py-[2px] rounded-full bg-amber-400 text-slate-950 shadow-sm leading-none tracking-tight">
                          ⭐ Renovar
                        </span>
                      ) : isLocked ? (
                        <span className="ml-auto text-[10px] font-extrabold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 leading-none tracking-tight flex items-center gap-1">
                          <FiLock size={10} />
                          Upgrade
                        </span>
                      ) : isEssentialItem && isTrial ? (
                        <span className="ml-auto text-[10px] font-bold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-xs flex items-center gap-1 leading-none tracking-tight">
                          ⭐ Essencial
                        </span>
                      ) : isEssentialItem && isBasic ? (
                        <span className="ml-auto text-[10px] font-extrabold uppercase px-2 py-[2px] rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 leading-none tracking-tight">
                          Upgrade
                        </span>
                      ) : null}
                    </div>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Bottom Sidebar User Info & Logout */}
        <div className={`p-3 border-t border-white/10 bg-black/20 ${sidebarOpen ? '' : 'flex flex-col items-center gap-2'}`}>
          {sidebarOpen ? (
            <div className="flex items-center justify-between gap-2">
              <div 
                onClick={() => navigate('/perfil')}
                className="flex items-center gap-2.5 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex-1"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover border border-cyan-400/30 flex-shrink-0" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm border border-cyan-300/30 flex-shrink-0">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
                <div className="overflow-hidden leading-tight text-left">
                  <p className="text-xs font-bold text-white truncate">{user?.name || 'Usuário'}</p>
                  <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
                </div>
              </div>

              <button
                onClick={handleLogout}
                title="Sair do sistema"
                className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer"
              >
                <FiLogOut size={16} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 w-full">
              <div 
                onClick={() => navigate('/perfil')}
                title={`${user?.name || 'Usuário'} - Meu Perfil`}
                className="cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover border border-cyan-400/30 flex-shrink-0" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm border border-cyan-300/30 flex-shrink-0">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
              </div>

              <button
                onClick={handleLogout}
                title="Sair do sistema"
                className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer"
              >
                <FiLogOut size={16} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ================= MOBILE DRAWER SIDEBAR ================= */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileOpen(false)}
          />

          <aside className="fixed top-0 bottom-0 left-0 w-72 bg-gradient-to-b from-[#071330] via-[#0C1E4E] to-[#122B68] text-white flex flex-col justify-between shadow-2xl z-10 border-r border-white/10 animate-in slide-in-from-left duration-200">
            <div>
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-tr from-blue-600 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25">
                    <FiDollarSign className="text-white" size={22} />
                  </div>
                  <div>
                    <h1 className="text-lg font-black text-white leading-none">Finance</h1>
                    <p className="text-[11px] text-cyan-300">Gestão Inteligente</p>
                  </div>
                </div>
                <button 
                  onClick={() => setMobileOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 cursor-pointer"
                >
                  <FiX size={20} />
                </button>
              </div>

              <nav className="p-4 space-y-1.5">
                {menuItems.map((item) => {
                  const hasSub = Array.isArray(item.subItems) && item.subItems.length > 0;
                  const isSubActive = hasSub && item.subItems.some(s => location.pathname === s.path || (s.path === '/carteiras/movimentacoes' && location.pathname === '/carteiras'));
                  const active = location.pathname === item.path || isSubActive;
                  const isEssentialItem = Boolean(item.isEssential);
                  const isAssinaturaItem = item.path === '/assinatura';
                  const isLocked = !isSubscriptionActive && !isAssinaturaItem && item.path !== '/perfil' && item.path !== '/notificacoes' && item.path !== '/gerenciar';
                  const isPulsingAssinatura = isAssinaturaItem && !isSubscriptionActive;

                  if (hasSub) {
                    return (
                      <div key={item.label} className="space-y-1">
                        <button
                          onClick={() => {
                            if (!isSubscriptionActive) {
                              setInactiveModalOpen(true);
                              setMobileOpen(false);
                              return;
                            }
                            setCarteirasExpanded(!carteirasExpanded);
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium text-sm transition-all cursor-pointer ${
                            active
                              ? 'bg-white/10 text-white font-semibold'
                              : 'text-slate-300 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`${isLocked ? 'p-0.5 rounded-lg ring-2 ring-amber-400/80 bg-amber-500/10' : ''}`}>
                              <item.icon size={19} className={active ? 'text-cyan-400' : isLocked ? 'text-amber-300' : 'text-slate-400'} />
                            </div>
                            <span>{item.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {isLocked ? (
                              <span className="text-[10px] font-extrabold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 leading-none tracking-tight flex items-center gap-1">
                                <FiLock size={10} />
                                Upgrade
                              </span>
                            ) : isEssentialItem && isTrial ? (
                              <span className="text-[10px] font-bold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-xs flex items-center gap-1 leading-none tracking-tight">
                                ⭐ Essencial
                              </span>
                            ) : isEssentialItem && isBasic ? (
                              <span className="text-[10px] font-extrabold uppercase px-2 py-[2px] rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 leading-none tracking-tight">
                                Upgrade
                              </span>
                            ) : null}
                            <div className="text-slate-400">
                              {carteirasExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                            </div>
                          </div>
                        </button>

                        {carteirasExpanded && (
                          <div className="pl-4 ml-4 space-y-1 border-l border-cyan-400/30">
                            {item.subItems.map((sub) => {
                              const subActive = location.pathname === sub.path || (sub.path === '/carteiras/movimentacoes' && location.pathname === '/carteiras');
                              return (
                                <button
                                  key={sub.path}
                                  onClick={() => {
                                    if (!isSubscriptionActive) {
                                      setInactiveModalOpen(true);
                                      setMobileOpen(false);
                                      return;
                                    }
                                    navigate(sub.path);
                                    setMobileOpen(false);
                                  }}
                                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg font-medium text-xs transition-all cursor-pointer ${
                                    subActive
                                      ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold'
                                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                                  }`}
                                >
                                  <sub.icon size={16} className={subActive ? 'text-white' : 'text-cyan-400'} />
                                  <span>{sub.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        setMobileOpen(false);
                        handleMenuClick(item.path, isEssentialItem);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium text-sm transition-all cursor-pointer ${
                        isPulsingAssinatura
                          ? 'border-2 border-amber-400 bg-amber-500/20 text-amber-300 font-bold shadow-lg shadow-amber-500/25 animate-pulse'
                          : active
                          ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-cyan-500/25 font-semibold'
                          : 'text-slate-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`${isLocked ? 'p-0.5 rounded-lg ring-2 ring-amber-400/80 bg-amber-500/10' : ''}`}>
                          <item.icon size={19} className={isPulsingAssinatura ? 'text-amber-300' : active ? 'text-white' : isLocked ? 'text-amber-300' : 'text-cyan-400'} />
                        </div>
                        <span>{item.label}</span>
                      </div>
                      {isPulsingAssinatura ? (
                        <span className="text-[10px] font-black uppercase px-2.5 py-[2px] rounded-full bg-amber-400 text-slate-950 shadow-sm leading-none tracking-tight">
                          ⭐ Renovar
                        </span>
                      ) : isLocked ? (
                        <span className="text-[10px] font-extrabold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 leading-none tracking-tight flex items-center gap-1">
                          <FiLock size={10} />
                          Upgrade
                        </span>
                      ) : isEssentialItem && isTrial ? (
                        <span className="text-[10px] font-bold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-xs flex items-center gap-1 leading-none tracking-tight">
                          ⭐ Essencial
                        </span>
                      ) : isEssentialItem && isBasic ? (
                        <span className="text-[10px] font-extrabold uppercase px-2 py-[2px] rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 leading-none tracking-tight">
                          Upgrade
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-4 border-t border-white/10 bg-black/20">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 overflow-hidden">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover border border-cyan-400/30" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className="overflow-hidden leading-tight text-left">
                    <p className="text-xs font-bold text-white truncate">{user?.name || 'Usuário'}</p>
                    <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
                  </div>
                </div>

                <button
                  onClick={handleLogout}
                  className="w-9 h-9 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-colors"
                >
                  <FiLogOut size={18} />
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ================= TOP NAVIGATION BAR (Header) ================= */}
      <header
        className={`sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/80 transition-all duration-300 ${
          sidebarOpen ? 'lg:pl-64' : 'lg:pl-20'
        }`}
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* Left: Hamburger Button (3 listras) & Page Breadcrumb */}
            <div className="flex items-center gap-3">
              {/* Desktop Hamburger Toggle */}
              <button
                onClick={toggleSidebar}
                title={sidebarOpen ? 'Ocultar menu lateral' : 'Expandir menu lateral'}
                className="hidden lg:flex items-center justify-center w-9 h-9 rounded-xl text-slate-600 hover:text-blue-900 hover:bg-blue-50 border border-slate-200/80 transition-all shadow-sm"
              >
                <FiMenu size={20} />
              </button>

              {/* Mobile Hamburger Toggle */}
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden flex items-center justify-center w-9 h-9 rounded-xl text-slate-600 hover:text-blue-900 hover:bg-blue-50 border border-slate-200/80 transition-all shadow-sm"
              >
                <FiMenu size={20} />
              </button>

              {/* Page Title */}
              <div className="flex items-center gap-2">
                <span className="text-base sm:text-lg font-bold text-[#1E3A8A] tracking-tight">
                  {getPageTitle()}
                </span>
              </div>
            </div>

            {/* Right: Database Indicator + Notifications + User Profile */}
            <div className="flex items-center gap-2 sm:gap-4">
              
              {/* Database Connection Indicator Button (Bolinha Verde) */}
              <button 
                onClick={() => setStatusModalOpen(true)}
                title="Clique para ver o status da conexão em tempo real"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/80 dark:hover:bg-slate-700/80 border border-slate-200/80 dark:border-white/10 text-xs font-medium text-slate-600 dark:text-slate-200 cursor-pointer transition-all shadow-sm active:scale-95"
              >
                <span className="relative flex h-2.5 w-2.5">
                  {dbConnected && (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  )}
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${dbConnected ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                </span>
                <span className="hidden sm:inline text-[11px] text-slate-600 dark:text-slate-300 font-semibold">
                  {dbConnected ? 'Online' : 'Offline'}
                </span>
              </button>

              {/* Notifications Bell Dropdown */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  title="Notificações"
                  className="relative p-2 rounded-xl text-slate-600 hover:text-blue-900 hover:bg-slate-100 transition-colors"
                >
                  <FiBell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white"></span>
                  )}
                </button>

                {/* Notifications Popover Dropdown */}
                {notificationsOpen && (
                  <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-in fade-in-50 zoom-in-95 duration-150">
                    <div className="p-3.5 bg-[#071330] text-white flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FiBell className="text-cyan-300" size={16} />
                        <span className="font-bold text-sm">Notificações</span>
                      </div>
                      <span className="text-[11px] text-cyan-200 bg-white/10 px-2 py-0.5 rounded-full">
                        {notifications.length} recentes
                      </span>
                    </div>

                    <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center text-xs text-slate-500">
                          Nenhuma notificação recente.
                        </div>
                      ) : (
                        notifications.map((n, idx) => (
                          <div key={idx} className="p-3 hover:bg-slate-50 transition-colors">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs font-semibold text-slate-800">
                                {n.type === 'due_date' ? 'Vencimento de Registro' :
                                 n.type === 'negative_balance' ? 'Alerta de Saldo Negativo' :
                                 n.type === 'goal_deadline' ? 'Prazo de Meta' : 'Aviso do Sistema'}
                              </p>
                              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                <FiClock size={10} />
                                {formatDate(n.created_at || n.date)}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-600 mt-1">
                              {n.message || `Notificação de ${n.type || 'registro financeiro'}`}
                            </p>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="p-2.5 bg-slate-50 border-t border-slate-100 text-center">
                      <button
                        onClick={() => {
                          setNotificationsOpen(false);
                          navigate('/notificacoes');
                        }}
                        className="text-xs font-bold text-blue-900 hover:underline inline-flex items-center gap-1"
                      >
                        <span>Ver todas as notificações</span>
                        <FiExternalLink size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Theme Toggle Button: Sol no tema claro, Lua no tema escuro */}
              <button
                onClick={toggleTheme}
                title={isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
                className="p-2 rounded-xl text-slate-600 hover:text-blue-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-cyan-400 dark:hover:bg-slate-800 transition-colors flex items-center justify-center"
              >
                {isDark ? (
                  <FiMoon size={20} className="text-cyan-400" />
                ) : (
                  <FiSun size={20} className="text-amber-500" />
                )}
              </button>

              {/* User Profile Picture & Dropdown Menu */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-slate-100 transition-all"
                >
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-blue-900/10"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <span className="hidden md:inline text-xs font-bold text-slate-700 max-w-[120px] truncate">
                    {user?.name?.split(' ')[0] || 'Usuário'}
                  </span>
                  <FiChevronDown size={14} className="text-slate-400" />
                </button>

                {/* Profile Dropdown Menu */}
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-in fade-in-50 zoom-in-95 duration-150">
                    {/* Header with User Info */}
                    <div className="p-4 bg-gradient-to-br from-[#071330] via-[#0C1E4E] to-[#122B68] text-white">
                      <div className="flex items-center gap-3">
                        {user?.picture ? (
                          <img src={user.picture} alt={user.name} className="w-11 h-11 rounded-full object-cover border border-cyan-400/40" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="w-11 h-11 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm border border-cyan-400/40">
                            {user?.name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div className="overflow-hidden leading-tight">
                          <p className="text-sm font-bold text-white truncate">{user?.name || 'Usuário'}</p>
                          <p className="text-xs text-cyan-200/80 truncate">{user?.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Actions */}
                    <div className="p-2 space-y-1">
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          navigate('/perfil');
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-900 rounded-xl transition-colors text-left"
                      >
                        <FiUser className="text-blue-900" size={16} />
                        <span>Meu perfil</span>
                      </button>

                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          navigate('/assinatura');
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-900 rounded-xl transition-colors text-left"
                      >
                        <FiCreditCard className="text-blue-900" size={16} />
                        <span>Assinatura</span>
                      </button>

                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          navigate('/sessoes-ativas');
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-900 rounded-xl transition-colors text-left"
                      >
                        <FiShield className="text-cyan-600" size={16} />
                        <span>Sessões ativas</span>
                      </button>

                      <div className="border-t border-slate-100 my-1"></div>

                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded-xl transition-colors text-left"
                      >
                        <FiLogOut size={16} />
                        <span>Sair</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </header>

      {/* ================= REALTIME CONNECTION STATUS POPUP MODAL ================= */}
      {statusModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/75 backdrop-blur-sm transition-opacity"
            onClick={() => setStatusModalOpen(false)}
          />

          {/* Modal Card */}
          <div className="relative w-full max-w-lg bg-[#111622] text-slate-100 rounded-2xl shadow-2xl border border-white/10 p-5 sm:p-6 z-10 animate-in fade-in-50 zoom-in-95 duration-150">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3.5">
              <div className="flex items-center gap-2.5">
                <FiWifi className="text-emerald-400" size={22} />
                <h2 className="text-lg font-bold text-emerald-400 tracking-tight">
                  Realtime conectado
                </h2>
              </div>
              <button
                onClick={() => setStatusModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                title="Fechar"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Banner Notice */}
            <div className="mt-1 mb-4 p-3.5 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 text-xs sm:text-sm font-medium leading-relaxed">
              Conexão ativa com o banco de dados. Eventos de transações, saldo e metas financeiras devem chegar em tempo real.
            </div>

            {/* Information Table */}
            <div className="border border-white/10 rounded-xl overflow-hidden divide-y divide-white/10 text-xs sm:text-sm bg-black/25">
              <div className="flex items-center justify-between p-3">
                <span className="text-slate-400 font-medium">Estado</span>
                <span className="font-mono text-slate-100 flex items-center gap-1.5 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  connected
                </span>
              </div>

              <div className="flex items-center justify-between p-3">
                <span className="text-slate-400 font-medium">WS host</span>
                <span className="font-mono text-slate-200">
                  ws.financeapp.com.br:443
                </span>
              </div>

              <div className="flex items-center justify-between p-3">
                <span className="text-slate-400 font-medium">TLS</span>
                <span className="font-mono text-slate-200">
                  wss (TLS)
                </span>
              </div>

              <div className="flex items-center justify-between p-3">
                <span className="text-slate-400 font-medium">App key</span>
                <span className="font-mono text-slate-300">
                  bTdjR3...Sw==
                </span>
              </div>

              <div className="flex items-center justify-between p-3">
                <span className="text-slate-400 font-medium">Build mode</span>
                <span className="font-mono text-slate-200">
                  production
                </span>
              </div>

              <div className="flex items-center justify-between p-3">
                <span className="text-slate-400 font-medium">Última conexão</span>
                <span className="font-mono text-slate-200 font-semibold">
                  {connectionTime}
                </span>
              </div>

              <div className="flex items-center justify-between p-3">
                <span className="text-slate-400 font-medium">Última queda</span>
                <span className="font-mono text-slate-400">
                  —
                </span>
              </div>
            </div>

            {/* History Section */}
            <div className="mt-4">
              <button
                onClick={() => setHistoryExpanded(!historyExpanded)}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors py-1 cursor-pointer"
              >
                <span>{historyExpanded ? '▼' : '►'}</span>
                <span>Histórico (1 transições)</span>
              </button>

              {historyExpanded && (
                <div className="mt-2 p-2.5 rounded-lg bg-black/40 border border-white/5 font-mono text-xs text-slate-300">
                  {connectionTime} · connecting → connected
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 text-xs sm:text-sm font-semibold transition-colors"
              >
                Recarregar página
              </button>

              <button
                onClick={() => setStatusModalOpen(false)}
                className="px-5 py-2 rounded-xl bg-[#7B82C7] hover:bg-[#6870BD] text-white text-xs sm:text-sm font-bold transition-colors shadow-md"
              >
                Fechar
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ================= MODAL DISCRETO: ASSINATURA INATIVA ================= */}
      <Dialog open={inactiveModalOpen} onOpenChange={setInactiveModalOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-6 text-center shadow-2xl">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto mb-3 border border-amber-500/20 shadow-inner">
            <FiLock size={26} />
          </div>
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-slate-900 dark:text-white text-center">
              Assinatura Inativa
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600 dark:text-slate-400 text-center pt-2 leading-relaxed">
              Este módulo está temporariamente bloqueado pois sua assinatura está expirada. Renove seu plano para desbloquear todas as funcionalidades, gráficos e lançamentos do Finance.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-center gap-2.5 pt-4 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setInactiveModalOpen(false)}
              className="flex-1 rounded-xl h-10 text-xs font-semibold cursor-pointer"
            >
              Fechar
            </Button>
            <Button
              type="button"
              onClick={() => {
                setInactiveModalOpen(false);
                navigate('/assinatura');
              }}
              className="flex-1 bg-gradient-to-r from-blue-900 via-blue-800 to-cyan-700 hover:from-blue-800 hover:to-cyan-600 text-white font-bold rounded-xl h-10 text-xs gap-1.5 shadow-md shadow-blue-900/20 cursor-pointer"
            >
              <FiCreditCard size={15} />
              Renovar Agora
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
